import React, { useEffect } from 'react';
import styled from 'styled-components';
import { MainContent } from '../App';

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, rgba(20, 30, 48, 0.95), rgba(36, 59, 85, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 60px 40px;
  text-align: center;
  backdrop-filter: blur(20px);
  max-width: 600px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 40px 30px;
    max-width: 90%;
  }
`;

const ErrorIcon = styled.div`
  font-size: 80px;
  color: #f44336;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 60px;
    margin-bottom: 20px;
  }
`;

const ErrorTitle = styled.h1`
  font-family: 'CocomatPro', sans-serif;
  font-size: 42px;
  font-weight: bold;
  color: white;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
  
  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const ErrorSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 30px 0;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const ErrorDetails = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 40px;
  
  p {
    margin: 0 0 15px 0;
  }
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 30px;
  margin: 30px 0;
  
  h3 {
    font-family: 'CocomatPro', sans-serif;
    font-size: 24px;
    color: #fcce00;
    margin: 0 0 20px 0;
    text-transform: uppercase;
    letter-spacing: 1px;
    
    @media (max-width: 768px) {
      font-size: 20px;
    }
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin: 20px 0;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  color: white;
  
  .icon {
    font-size: 24px;
    color: #fcce00;
    min-width: 30px;
  }
  
  .info {
    font-weight: 500;
  }
  
  a {
    color: white;
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: #fcce00;
    }
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    
    .icon {
      font-size: 20px;
    }
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    text-align: center;
    gap: 5px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const ActionButton = styled.button`
  font-family: 'CocomatPro', sans-serif;
  background: linear-gradient(100deg, #fcce00, #f7931e);
  border: none;
  border-radius: 25px;
  padding: 15px 35px;
  color: white;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  text-transform: uppercase;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(252, 206, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 14px;
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
  }
`;

const ErrorPage: React.FC = () => {
  useEffect(() => {
    // Update meta tags for social media previews
    const updateMetaTags = () => {
      // Update Open Graph tags
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      
      if (ogTitle) ogTitle.setAttribute('content', 'Error en el Pago - FootballClub');
      if (ogDescription) ogDescription.setAttribute('content', 'Ha ocurrido un problema procesando tu compra. No te preocupes, no se ha realizado ningún cargo. Contacta con nuestro soporte.');
      if (ogImage) ogImage.setAttribute('content', `${process.env.REACT_APP_DOMAIN || window.location.origin}/sharing-image-whatsapp.jpeg`);
      
      // Update Twitter Card tags
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      
      if (twitterTitle) twitterTitle.setAttribute('content', 'Error en el Pago - FootballClub');
      if (twitterDescription) twitterDescription.setAttribute('content', 'Ha ocurrido un problema procesando tu compra. No te preocupes, no se ha realizado ningún cargo. Contacta con nuestro soporte.');
      if (twitterImage) twitterImage.setAttribute('content', `${process.env.REACT_APP_DOMAIN || window.location.origin}/sharing-image-whatsapp.jpeg`);
      
      // Update page title
      document.title = 'Error en el Pago - FootballClub';
    };
    
    updateMetaTags();
  }, []);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  const handleTryAgain = () => {
    window.history.back();
  };

  return (
    <MainContent>
      <CenteredContainer>
        <ErrorMessage>
            <ErrorIcon>⚠</ErrorIcon>
            <ErrorTitle>Error en el Pago</ErrorTitle>
            <ErrorSubtitle>
              Lo sentimos, ha ocurrido un problema procesando tu compra
            </ErrorSubtitle>
            
            <ErrorDetails>
              <p>
                No te preocupes, no se ha realizado ningún cargo a tu tarjeta.
              </p>
              <p>
                Puedes intentar nuevamente o contactar con nuestro equipo de soporte 
                para recibir ayuda personalizada.
              </p>
            </ErrorDetails>
            
            <ContactInfo>
              <h3>¿Necesitas Ayuda?</h3>
              
              <ContactItem>
                <span className="icon">📞</span>
                <div className="info">
                  <a href={`tel:${process.env.REACT_APP_CONTACT_PHONE || '+34900123456'}`}>
                    {process.env.REACT_APP_CONTACT_PHONE || '+34 900 123 456'}
                  </a>
                </div>
              </ContactItem>
              
              <ContactItem>
                <span className="icon">✉️</span>
                <div className="info">
                  <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'soporte@footballclub.es'}`}>
                    {process.env.REACT_APP_CONTACT_EMAIL || 'soporte@footballclub.es'}
                  </a>
                </div>
              </ContactItem>
              
              <ContactItem>
                <span className="icon">🕒</span>
                <div className="info">
                  Horario: Lunes a Viernes 9:00 - 18:00
                </div>
              </ContactItem>
            </ContactInfo>
            
            <ButtonGroup>
              <ActionButton onClick={handleTryAgain}>
                Intentar de nuevo
              </ActionButton>
              <SecondaryButton onClick={handleBackToHome}>
                Volver al inicio
              </SecondaryButton>
            </ButtonGroup>
          </ErrorMessage>
      </CenteredContainer>
    </MainContent>
  );
};

export default ErrorPage;